import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BadgeDollarSign, SearchIcon, CheckCircle2Icon, TruckIcon, CalendarIcon, HashIcon, XIcon, IndianRupee, ArrowRightFromLine, BanknoteIcon, QrCodeIcon, TicketIcon, PrinterIcon } from 'lucide-react';
import { PaymentModal } from '../components/PaymentModal';
import { BillReceipt } from '../components/BillReceipt';
interface VehicleEntry {
  gatePassNo: string;
  vehicleNo: string;
  vehicleType: string;
  user: string;
  dateTime: string;
}
const vehicleRecords: VehicleEntry[] = [{
  gatePassNo: 'GP-2026-0001',
  vehicleNo: 'MH-12-AB-1234',
  vehicleType: 'Truck',
  user: 'Rajesh Kumar',
  dateTime: '2026-04-15 08:30 AM'
}, {
  gatePassNo: 'GP-2026-0002',
  vehicleNo: 'MH-14-CD-5678',
  vehicleType: 'Car',
  user: 'Priya Sharma',
  dateTime: '2026-04-15 09:15 AM'
}, {
  gatePassNo: 'GP-2026-0003',
  vehicleNo: 'GJ-05-EF-9012',
  vehicleType: 'Van',
  user: 'Amit Patel',
  dateTime: '2026-04-15 09:45 AM'
}, {
  gatePassNo: 'GP-2026-0004',
  vehicleNo: 'DL-01-GH-3456',
  vehicleType: 'Trailer',
  user: 'Sunil Verma',
  dateTime: '2026-04-15 10:00 AM'
}, {
  gatePassNo: 'GP-2026-0005',
  vehicleNo: 'RJ-14-IJ-7890',
  vehicleType: 'Truck',
  user: 'Deepak Singh',
  dateTime: '2026-04-15 10:30 AM'
}];
const feeStructure: Record<string, {
  parking: number;
  entry: number;
}> = {
  Truck: {
    parking: 200,
    entry: 150
  },
  Car: {
    parking: 100,
    entry: 50
  },
  Bike: {
    parking: 50,
    entry: 20
  },
  Van: {
    parking: 150,
    entry: 100
  },
  Trailer: {
    parking: 250,
    entry: 200
  }
};
type PaymentMethod = 'Cash' | 'QR' | 'Coupon';
export function BillingAndExitPage({
  userName
}: {
  userName: string;
}) {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResult, setSearchResult] = useState<VehicleEntry | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [parkingFee, setParkingFee] = useState<number>(0);
  const [entryFee, setEntryFee] = useState<number>(0);
  const [otherCharges, setOtherCharges] = useState<number>(0);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('Cash');
  const [paymentConfirmed, setPaymentConfirmed] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState<'QR' | 'Coupon' | null>(null);
  const [billGenerated, setBillGenerated] = useState(false);
  const [billNumber, setBillNumber] = useState('');
  const [exitConfirmed, setExitConfirmed] = useState(false);
  const [showReceipt, setShowReceipt] = useState(false);
  const total = parkingFee + entryFee + otherCharges;
  useEffect(() => {
    if (searchResult) {
      const fees = feeStructure[searchResult.vehicleType] || {
        parking: 0,
        entry: 0
      };
      setParkingFee(fees.parking);
      setEntryFee(fees.entry);
    }
  }, [searchResult]);
  const handleSearch = () => {
    if (!searchQuery.trim()) return;
    const q = searchQuery.trim().toUpperCase();
    const found = vehicleRecords.find(r => r.gatePassNo.toUpperCase() === q || r.vehicleNo.toUpperCase() === q);
    setSearchResult(found || null);
    setNotFound(!found);
    setBillGenerated(false);
    setExitConfirmed(false);
    setHasSearched(true);
    setOtherCharges(0);
    setPaymentMethod('Cash');
    setPaymentConfirmed(false);
  };
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };
  const handleSelectPayment = (method: PaymentMethod) => {
    setPaymentMethod(method);
    setPaymentConfirmed(method === 'Cash');
    if (method === 'QR' || method === 'Coupon') {
      setShowPaymentModal(method);
    }
  };
  const handlePaymentConfirmed = () => {
    setPaymentConfirmed(true);
    setShowPaymentModal(null);
  };
  const handleGenerateBill = () => {
    setBillNumber(`INV-2026-${Math.floor(1000 + Math.random() * 9000)}`);
    setBillGenerated(true);
  };
  const handleConfirmExit = () => {
    setExitConfirmed(true);
  };
  const handleReset = () => {
    setSearchQuery('');
    setSearchResult(null);
    setNotFound(false);
    setBillGenerated(false);
    setExitConfirmed(false);
    setHasSearched(false);
    setPaymentConfirmed(false);
    setShowPaymentModal(null);
    setShowReceipt(false);
  };
  const now = new Date();
  const exitTime = now.toLocaleString('en-IN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
  return <div className="w-full max-w-4xl mx-auto" data-id="element-264">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.5
    }} className="bg-white rounded-2xl shadow-xl shadow-slate-200/50 border border-slate-100 p-5 xs:p-6 sm:p-8" data-id="element-265">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6" data-id="element-266">
          <div className="bg-brand-teal/10 p-2 rounded-xl border border-brand-teal/20" data-id="element-267">
            <BadgeDollarSign className="w-6 h-6 text-brand-teal" data-id="element-268" />
          </div>
          <div data-id="element-269">
            <h2 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight" data-id="element-270">
              Billing & Exit
            </h2>
            <p className="text-sm text-slate-500" data-id="element-271">
              Generate bill and process final exit in one flow.
            </p>
          </div>
        </div>

        {/* Search Bar */}
        <div className="flex gap-3 mb-6" data-id="element-272">
          <div className="relative flex-1" data-id="element-273">
            <SearchIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-slate-400 pointer-events-none" data-id="element-274" />
            <input type="text" placeholder="Enter Gate Pass No. or Vehicle No. (e.g. GP-2026-0001)" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} onKeyDown={handleKeyDown} className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-200 bg-slate-50 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-teal/30 focus:border-brand-teal transition-all" data-id="element-275" />
          </div>
          <button onClick={handleSearch} className="px-5 py-3 bg-brand-teal text-white rounded-xl font-medium text-sm hover:bg-brand-tealHover transition-colors whitespace-nowrap" data-id="element-276">
            Search
          </button>
        </div>

        <AnimatePresence mode="wait" data-id="element-277">
          {hasSearched && notFound && <motion.div key="not-found" initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} className="border border-red-200 bg-red-50 rounded-xl p-6 text-center" data-id="element-278">
              <div className="text-red-400 mb-2" data-id="element-279">
                <XIcon className="w-10 h-10 mx-auto" data-id="element-280" />
              </div>
              <p className="text-red-700 font-semibold text-sm" data-id="element-281">
                No record found
              </p>
            </motion.div>}

          {searchResult && !billGenerated && !exitConfirmed && <motion.div key="result" initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} className="border border-slate-200 rounded-xl overflow-hidden" data-id="element-282">
              <div className="bg-slate-50 px-5 py-3 border-b border-slate-200" data-id="element-283">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider" data-id="element-284">
                  Step 1: Generate Bill
                </p>
              </div>
              <div className="p-5" data-id="element-285">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6" data-id="element-286">
                  <div className="flex items-start gap-3" data-id="element-287">
                    <div className="bg-brand-teal/10 p-2 rounded-lg" data-id="element-288">
                      <HashIcon className="w-4 h-4 text-brand-teal" data-id="element-289" />
                    </div>
                    <div data-id="element-290">
                      <p className="text-xs text-slate-400 font-medium" data-id="element-291">
                        Gate Pass No.
                      </p>
                      <p className="text-sm font-bold text-slate-800 font-mono" data-id="element-292">
                        {searchResult.gatePassNo}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3" data-id="element-293">
                    <div className="bg-blue-50 p-2 rounded-lg" data-id="element-294">
                      <TruckIcon className="w-4 h-4 text-blue-600" data-id="element-295" />
                    </div>
                    <div data-id="element-296">
                      <p className="text-xs text-slate-400 font-medium" data-id="element-297">
                        Vehicle No.
                      </p>
                      <p className="text-sm font-bold text-slate-800 font-mono" data-id="element-298">
                        {searchResult.vehicleNo}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="border-t border-slate-200 pt-6" data-id="element-299">
                  <div className="space-y-4" data-id="element-300">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-301">
                      <div data-id="element-302">
                        <label className="block text-xs font-semibold text-slate-500 mb-1.5" data-id="element-303">
                          Parking Fee (₹)
                        </label>
                        <div className="relative" data-id="element-304">
                          <IndianRupee className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-305" />
                          <input type="number" value={parkingFee} disabled className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-sm text-slate-400 cursor-not-allowed" data-id="element-306" />
                        </div>
                      </div>
                      <div data-id="element-307">
                        <label className="block text-xs font-semibold text-slate-500 mb-1.5" data-id="element-308">
                          Entry Fee (₹)
                        </label>
                        <div className="relative" data-id="element-309">
                          <IndianRupee className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-310" />
                          <input type="number" value={entryFee} disabled className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-sm text-slate-400 cursor-not-allowed" data-id="element-311" />
                        </div>
                      </div>
                    </div>
                    <div data-id="element-312">
                      <label className="block text-xs font-semibold text-slate-500 mb-1.5" data-id="element-313">
                        Other Charges (₹)
                      </label>
                      <div className="relative" data-id="element-314">
                        <IndianRupee className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-315" />
                        <input type="number" min="0" value={otherCharges || ''} onChange={e => setOtherCharges(Number(e.target.value))} className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 bg-white text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-brand-teal/30 focus:border-brand-teal transition-all" placeholder="0.00" data-id="element-316" />
                      </div>
                    </div>

                    {/* Payment Method */}
                    <div className="pt-2" data-id="element-317">
                      <label className="block text-xs font-semibold text-slate-500 mb-2" data-id="element-318">
                        Payment Method
                      </label>
                      <div className="flex gap-2" data-id="element-319">
                        <button onClick={() => handleSelectPayment('Cash')} className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium transition-colors border ${paymentMethod === 'Cash' && paymentConfirmed ? 'bg-brand-teal text-white border-brand-teal' : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100'}`} data-id="element-320">
                          <BanknoteIcon className="w-4 h-4" data-id="element-321" />
                          Cash
                        </button>
                        <button onClick={() => handleSelectPayment('QR')} className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium transition-colors border ${paymentMethod === 'QR' && paymentConfirmed ? 'bg-brand-teal text-white border-brand-teal' : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100'}`} data-id="element-322">
                          <QrCodeIcon className="w-4 h-4" data-id="element-323" />
                          QR Code
                        </button>
                        <button onClick={() => handleSelectPayment('Coupon')} className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium transition-colors border ${paymentMethod === 'Coupon' && paymentConfirmed ? 'bg-brand-teal text-white border-brand-teal' : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100'}`} data-id="element-324">
                          <TicketIcon className="w-4 h-4" data-id="element-325" />
                          Coupon
                        </button>
                      </div>
                      {paymentConfirmed && paymentMethod !== 'Cash' && <motion.p initial={{
                    opacity: 0,
                    y: -5
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} className="text-xs text-green-600 font-medium mt-2 flex items-center gap-1" data-id="element-326">
                          <CheckCircle2Icon className="w-3.5 h-3.5" data-id="element-327" />
                          {paymentMethod === 'QR' ? 'QR payment confirmed' : 'Coupon applied successfully'}
                        </motion.p>}
                    </div>

                    <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-200 mt-6" data-id="element-328">
                      <span className="font-bold text-slate-700" data-id="element-329">
                        Total Amount
                      </span>
                      <span className="text-xl font-bold text-brand-teal" data-id="element-330">
                        ₹{total.toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex gap-3 pt-6" data-id="element-331">
                  <button onClick={handleGenerateBill} disabled={total <= 0 || !paymentConfirmed} className="flex-1 inline-flex items-center justify-center gap-2 bg-brand-teal text-white px-4 py-3 rounded-xl font-medium text-sm hover:bg-brand-tealHover transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-332">
                    <BadgeDollarSign className="w-4 h-4" data-id="element-333" />
                    Generate Bill & Continue
                  </button>
                  <button onClick={handleReset} className="inline-flex items-center justify-center gap-2 bg-white text-slate-600 px-4 py-3 rounded-xl font-medium text-sm border border-slate-200 hover:bg-slate-50 transition-colors" data-id="element-334">
                    Cancel
                  </button>
                </div>
              </div>
            </motion.div>}

          {/* Bill Generated & Exit Auto Popup */}
          {billGenerated && !exitConfirmed && searchResult && <motion.div key="exit-popup" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="border border-brand-teal/20 bg-brand-teal/5 rounded-xl overflow-hidden" data-id="element-335">
              <div className="bg-brand-teal/10 px-5 py-3 border-b border-brand-teal/20 flex items-center gap-2" data-id="element-336">
                <CheckCircle2Icon className="w-4 h-4 text-brand-teal" data-id="element-337" />
                <p className="text-xs font-semibold text-brand-teal uppercase tracking-wider" data-id="element-338">
                  Bill Generated — Step 2: Process Exit
                </p>
              </div>
              <div className="p-6 text-center" data-id="element-339">
                <p className="text-sm text-slate-600 mb-2" data-id="element-340">
                  Invoice{' '}
                  <span className="font-mono font-bold text-slate-800" data-id="element-341">
                    {billNumber}
                  </span>{' '}
                  created for ₹{total.toFixed(2)}.
                </p>
                <div className="flex items-center justify-center gap-4 text-xs text-brand-teal mb-4 font-medium" data-id="element-342">
                  <span data-id="element-343">Paid via {paymentMethod}</span>
                </div>

                <button onClick={() => setShowReceipt(true)} className="inline-flex items-center gap-2 bg-white text-brand-teal px-4 py-2 rounded-xl font-medium text-sm border border-brand-teal/30 hover:bg-brand-teal/5 transition-colors mb-6" data-id="element-344">
                  <PrinterIcon className="w-4 h-4" data-id="element-345" />
                  Print Bill
                </button>

                <h3 className="text-lg font-bold text-slate-900 mb-6" data-id="element-346">
                  Ready for Final Exit
                </h3>

                <motion.button initial={{
              scale: 0.9
            }} animate={{
              scale: 1
            }} transition={{
              type: 'spring',
              bounce: 0.5
            }} onClick={handleConfirmExit} className="inline-flex items-center justify-center gap-2 bg-rose-600 text-white px-8 py-4 rounded-xl font-bold text-base hover:bg-rose-700 transition-colors shadow-lg shadow-rose-600/20" data-id="element-347">
                  <ArrowRightFromLine className="w-5 h-5" data-id="element-348" />
                  Process Final Exit
                </motion.button>
              </div>
            </motion.div>}

          {/* Final Success State */}
          {exitConfirmed && searchResult && <motion.div key="confirmed" initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="border border-green-200 bg-green-50 rounded-xl p-8 text-center" data-id="element-349">
              <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            bounce: 0.5,
            delay: 0.1
          }} data-id="element-350">
                <CheckCircle2Icon className="w-14 h-14 text-green-500 mx-auto mb-3" data-id="element-351" />
              </motion.div>
              <h3 className="text-lg font-bold text-green-800 mb-1" data-id="element-352">
                Billing & Exit Completed Successfully
              </h3>
              <p className="text-sm text-green-600 mb-1" data-id="element-353">
                Gate Pass{' '}
                <span className="font-mono font-bold" data-id="element-354">
                  {searchResult.gatePassNo}
                </span>{' '}
                — Vehicle{' '}
                <span className="font-mono font-bold" data-id="element-355">
                  {searchResult.vehicleNo}
                </span>
              </p>
              <p className="text-xs text-green-500" data-id="element-356">
                Exit recorded at {exitTime}
              </p>
              <div className="flex items-center justify-center gap-3 mt-5" data-id="element-357">
                <button onClick={() => setShowReceipt(true)} className="inline-flex items-center gap-2 bg-white text-green-700 px-5 py-2.5 rounded-xl font-medium text-sm border border-green-300 hover:bg-green-100 transition-colors" data-id="element-358">
                  <PrinterIcon className="w-4 h-4" data-id="element-359" />
                  Print Bill
                </button>
                <button onClick={handleReset} className="inline-flex items-center gap-2 bg-green-600 text-white px-5 py-2.5 rounded-xl font-medium text-sm hover:bg-green-700 transition-colors" data-id="element-360">
                  Process Another Vehicle
                </button>
              </div>
            </motion.div>}

          {!hasSearched && <motion.div key="empty" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="border-2 border-dashed border-slate-200 rounded-xl p-8 text-center bg-slate-50" data-id="element-361">
              <SearchIcon className="w-10 h-10 text-slate-300 mx-auto mb-3" data-id="element-362" />
              <p className="text-slate-500 text-sm font-medium" data-id="element-363">
                Search for a gate pass to begin
              </p>
            </motion.div>}
        </AnimatePresence>
      </motion.div>

      {/* Payment Modal */}
      <AnimatePresence data-id="element-364">
        {showPaymentModal && <PaymentModal type={showPaymentModal} amount={total} onConfirm={handlePaymentConfirmed} onClose={() => setShowPaymentModal(null)} data-id="element-365" />}
      </AnimatePresence>

      {/* Bill Receipt Print Modal */}
      {showReceipt && searchResult && <BillReceipt billNo={billNumber} gatePassNo={searchResult.gatePassNo} vehicleNo={searchResult.vehicleNo} amount={total} paymentMode={paymentMethod} dateTime={new Date().toLocaleString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    })} onClose={() => setShowReceipt(false)} data-id="element-366" />}
    </div>;
}